/**
 * Users API — Endpoint 9
 *
 * API 9 : GET /api/users/live → All users with active subscriptions
 */
import { apiFetch, buildUrl } from "./apiClient";

/**
 * API 9 — Fetch all live (active subscription) users.
 * Used to show the live user count chip on the dashboard header.
 *
 * @param {number} [limit=20]
 * @param {string} [cursor]
 * @returns {Promise<{ data: User[], meta: { cursor?: string } }>}
 *
 * User shape:
 * { userId, displayName, email, role, org, subscriptionStatus, planType, createdAt, updatedAt }
 */
export async function fetchLiveUsers(limit = 20, cursor) {
    const path = buildUrl("/api/users/live", { limit, cursor });
    return apiFetch(path);
}

/**
 * Fetch ALL live users and return client entries with clientId
 * for the ExecuteReleaseModal client picker. Backend expects client IDs in execute payload.
 *
 * One entry per client (API returns full client objects with clientId).
 * Paginates through every page (safety-capped at 20 pages).
 *
 * @returns {Promise<Array<{ org: string, center: string, clientId: string, displayName: string, email: string, planType: string }>>}
 */
export async function fetchAllLiveOrgs() {
    const MAX_PAGES = 20;
    let allUsers = [];
    let cursor;
    let page = 0;

    do {
        const result = await apiFetch(
            buildUrl("/api/users/live", { limit: 100, ...(cursor != null ? { cursor } : {}) })
        );
        const pageData = result?.data || [];
        allUsers = [...allUsers, ...pageData];
        cursor = result?.meta?.cursor || null;
        page++;
    } while (cursor && page < MAX_PAGES);

    // One entry per client (keep clientId for API payload); only include entries with clientId
    const clients = [];
    for (const u of allUsers) {
        const clientId = (u.clientId || "").trim();
        if (!clientId) continue;
        const org = (u.org || "").trim();
        const center = (u.center || "").trim();
        const customerName = (u.customerName || u.displayName || org || "").trim() || clientId;
        clients.push({
            org: org || "—",
            center,
            clientId,
            displayName: u.displayName || u.customerName || org || clientId,
            customerName,
            email: u.email || "",
            planType: u.planType || "",
        });
    }
    return clients.sort((a, b) => (a.customerName || a.displayName || a.clientId).localeCompare(b.customerName || b.displayName || b.clientId));
}

